import isEmpty from 'lodash-es/isEmpty';

import type { Problem } from 'cadenza/api-client/dto/problem-dto';
import type { TextDataViewAttributeValueDto } from 'cadenza/api-client/dto/text-data-view-attribute-value-dto';
import { getTextViewData } from 'cadenza/api-client/workbook-api';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { createAsyncThunk } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import { getWorkbookNameAndHash, getDataViewsByViewId, slice } from 'cadenza/workbook/workbook.state';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';

const SLICE_PREFIX = 'workbookSlice';

interface TextViewDataLoadingState {
  state: 'SUCCESS' | 'FAILURE';
  error?: Error | Problem;
}

export interface WorkbookTextViewState {
  textViewData: Record<ViewId, Record<ActiveAttributeId, TextDataViewAttributeValueDto>>;
  textViewDataLoadingState: Record<ViewId, TextViewDataLoadingState>;
}

export function createInitialTextViewState(): WorkbookTextViewState {
  return {
    textViewData: {},
    textViewDataLoadingState: {},
  };
}

/**
 * When using observers on this selector, please make sure to observe by value!
 */
export function getTextViewDataForViewId(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
): Record<ActiveAttributeId, TextDataViewAttributeValueDto> {
  return slice(state).textViewData[viewId];
}

export function isQueryResultEmptyForTextViewId(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const haveAttributesForQuery = getDataViewsByViewId(state, viewId).some((dv) => dv.attributes.ATTRIBUTES.length > 0);
  return haveAttributesForQuery && isEmpty(getTextViewDataForViewId(state, viewId));
}

export function getTextViewDataLoadingStateForViewId(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
): TextViewDataLoadingState {
  return slice(state).textViewDataLoadingState[viewId];
}

export const fetchTextViewData = createAsyncThunk(
  SLICE_PREFIX + '/fetchTextViewData',
  async (viewId: ViewId, thunkAPI) => {
    try {
      const workbookNameAndHash = getWorkbookNameAndHash(thunkAPI.getState());
      const textViewData = await getTextViewData(workbookNameAndHash, viewId, thunkAPI.signal);
      return thunkAPI.fulfillWithValue({ viewId, textViewData });
    } catch (error) {
      return thunkAPI.rejectWithValue({ viewId, error });
    }
  },
);

export function addTextViewExtraReducers(builder: ActionReducerMapBuilder<WorkbookState>) {
  builder
    .addCase(fetchTextViewData.fulfilled, (state, action) => {
      const { viewId, textViewData } = action.payload;
      slice(state).textViewData[viewId] = textViewData;
      slice(state).textViewDataLoadingState[viewId] = { state: 'SUCCESS' };
    })
    .addCase(fetchTextViewData.rejected, (state, action) => {
      const { viewId, error } = action.payload as { viewId: ViewId; error: Error | Problem };
      slice(state).textViewDataLoadingState[viewId] = { state: 'FAILURE', error };
    });
}
